import { Flex, Select } from '@mantine/core';
import { IconFile, IconFolder } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { UploadedFile } from '../../hooks/file-uploads/types';
import { useFileUploads } from '../../hooks/file-uploads/useFileUploads';
import { FileManagementModal } from '../modals/FileManagementModal';

type Props = {
  opened: boolean;
  close: () => void;
  file: UploadedFile | null;
  onFileMoved: (file: UploadedFile, parentId: string | null | undefined) => Promise<void>;
};

export default function MoveFileModal({ opened, close, file, onFileMoved }: Props) {
  const { files, refetchFiles } = useFileUploads();
  const { client } = useCurrentClient();

  useEffect(() => {
    if (!opened) {
      return;
    }

    refetchFiles();
  }, [opened, refetchFiles]);

  const [selectedFolderId, setSelectedFolder] = useState<string | undefined | null>(
    file?.file.parentId,
  );
  const [isMoving, setIsMoving] = useState(false);

  const rootFolder = { value: '', label: `All ${client?.firstName}'s Documents` };

  const folders = [
    ...files
      .filter(f => f.isFolder)
      .map(folder => ({
        value: folder.id,
        label: folder.file.fileName,
      })),
    rootFolder,
  ];

  const handleMove = async () => {
    if (!file) return;
    setIsMoving(true);
    try {
      await onFileMoved(file, selectedFolderId || null);
      close();
    } finally {
      setIsMoving(false);
    }
  };

  const handleSelectFolder = (folderId: string | undefined | null) => {
    setSelectedFolder(folderId);
  };

  const selectedFolder = folders.find(folder => folder.value === selectedFolderId);
  const selectionValue = selectedFolder || rootFolder;

  const currentFolderName = file?.file.parentId
    ? folders.find(f => f.value === file.file.parentId)?.label || 'Unknown'
    : `All ${client?.firstName}'s Documents`;

  return (
    <FileManagementModal
      opened={opened}
      onClose={close}
      title="Where would you like to move the document?"
      subtitle="Please select a folder"
      icon={<IconFile size={24} />}
      onConfirm={handleMove}
      confirmDisabled={selectedFolderId === file?.file.parentId && file?.file?.isFiled}
      confirmLoading={isMoving}
    >
      <Select
        label={currentFolderName}
        placeholder="Select folder"
        data={folders}
        value={selectionValue.value}
        clearable
        onChange={handleSelectFolder}
        styles={{
          label: {
            fontWeight: 500,
          },
        }}
        leftSection={
          <Flex px="md">
            <IconFolder size={16} color="var(--mantine-color-gray-7)" />
          </Flex>
        }
      />
    </FileManagementModal>
  );
}
